// ═══════════════ REFERENCE NUMBER PREFIXES ═══════════════
export const REF_PREFIXES = {
  DRIVER: 'D',        // D001, D002, ...
  RIDER: 'C',         // C001, C002, ... (Customer)
  VEHICLE: 'V',       // V001, V002, ...
  BOOKING: 'BK',      // BK00001, ...
  TRIP: 'TR',         // TR00001, ...
  TICKET: 'TK',       // TK00001, ...
  CLAIM: 'CLM',       // CLM-0001, ...
  LOST_ITEM: 'LI',    // LI-0001, ...
  INVOICE: 'INV',     // INV-00001, ...
  // Batch 12 — recurring bookings
  SERIES: 'SR',       // SR00001, ...
  // Batch 13 — multi-vehicle dispatch
  VEHICLE_GROUP: 'VG',// VG00001, ...
  // Batch 10 — compliance
  INCIDENT: 'IN',     // IN00001, ...
  COMPLAINT: 'CP',    // CP00001, ...
};

// ═══════════════ USER ROLES ═══════════════
export const USER_ROLES = {
  RIDER: 'rider',
  DRIVER: 'driver',
  ADMIN: 'admin',
  SUPER_ADMIN: 'superAdmin',
};

// ═══════════════ USER STATUS ═══════════════
export const USER_STATUS = {
  PENDING: 'pending',           // Registered, awaiting verification
  EMAIL_VERIFIED: 'emailVerified',
  PHONE_VERIFIED: 'phoneVerified',
  FULLY_VERIFIED: 'fullyVerified', // Both email + phone verified
  ACTIVE: 'active',             // Approved and operational
  SUSPENDED: 'suspended',
  DEACTIVATED: 'deactivated',
};

// ═══════════════ DRIVER APPLICATION STATUS ═══════════════
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  DOCUMENTS_SUBMITTED: 'documentsSubmitted',
  UNDER_REVIEW: 'underReview',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CHANGES_REQUESTED: 'changesRequested',
};

// ═══════════════ VEHICLE TIERS ═══════════════
export const VEHICLE_TIERS = {
  GO: { id: 'go', name: 'Reservo Go', maxPassengers: 4, description: 'Sedan — comfortable everyday rides' },
  COMFY: { id: 'comfy', name: 'Reservo Comfy', maxPassengers: 4, description: 'Premium sedan — extra comfort' },
  PLUS: { id: 'plus', name: 'Reservo Plus', maxPassengers: 6, description: 'SUV — more space for groups & luggage' },
  VIP: { id: 'vip', name: 'Reservo VIP', maxPassengers: 4, description: 'Luxury — Mercedes, BMW, Audi' },
  VIP_PLUS: { id: 'vipPlus', name: 'Reservo VIP+', maxPassengers: 6, description: 'Luxury SUV — Mercedes GLS, BMW X7, Audi Q7' },
};

// ═══════════════ VIP ELIGIBLE BRANDS ═══════════════
export const VIP_ELIGIBLE_BRANDS = [
  'Mercedes-Benz', 'Mercedes', 'BMW', 'Audi',
  // Add more luxury brands as needed:
  // 'Lexus', 'Jaguar', 'Porsche', 'Tesla', 'Maserati', 'Bentley', 'Rolls-Royce',
];

// ═══════════════ COMPLIANCE (Batch 10) ═══════════════
// Retention and consent constants for NSW Point to Point compliance.
// RECORD_RETENTION_YEARS: 7 years reflects the LONGEST overlapping obligation
// (ATO tax records ~5yr + Limitation Act 1969 (NSW) civil-claim period 6yr), not a
// single figure mandated by the P2P Commissioner. Safe defensible maximum.
export const COMPLIANCE_CONFIG = {
  // Records retention period — incidents, complaints, consents, audit log
  RECORD_RETENTION_YEARS: 7,
  // Driver consent wording version — bump when you change wording in DriverRegistrationWizard.js
  // [VERIFY: must match CONSENT_WORDING_VERSION in DriverRegistrationWizard.js]
  CONSENT_WORDING_VERSION: 'v1.0-2026-01-01',
  // SLA (in hours/days) for complaint handling
  COMPLAINT_ACK_HOURS: 24,
  COMPLAINT_RESPONSE_DAYS: 7,
  COMPLAINT_RESOLUTION_DAYS: 28,
  // INTERNAL SLA TARGETS for notifying the Commissioner — these are Reservo's own
  // operational targets, NOT a statutory deadline. The NSW P2P legal standard for
  // notifiable occurrences is "as soon as practicable after becoming aware".
  // Do not present these hour values to users/staff as the legal requirement.
  CRITICAL_INCIDENT_REPORTING_HOURS: 24,
  HIGH_INCIDENT_REPORTING_HOURS: 168,
};

// ═══════════════ AUTHENTICATION ═══════════════
export const AUTH_CONFIG = {
  MAX_LOGIN_ATTEMPTS: 3,
  LOCKOUT_DURATION_MINUTES: 15,
  OTP_EXPIRY_MINUTES: 5,
  EMAIL_LINK_EXPIRY_HOURS: 24,
  MAX_OTP_REQUESTS_PER_HOUR: 5,
  ADMIN_PIN_LENGTH: 4,
};

// ═══════════════ TRIP PIN VERIFICATION ═══════════════
// Generated per booking, shared with rider only. Driver must enter
// the PIN to start the trip — prevents fraudulent trip starts.
export const TRIP_PIN_CONFIG = {
  PIN_LENGTH: 4,                    // 4-digit numeric code
  MAX_ATTEMPTS: 3,                  // Lock after 3 wrong entries
  LOCKOUT_DURATION_MINUTES: 5,      // 5min lockout on failed attempts
  DISPLAY_TO_RIDER_ONLY: true,      // Never shown to driver
  REQUIRED_AT_STATUS: 'arrived',    // Driver must be GPS-verified "Arrived" before PIN entry
};

// ═══════════════ GPS GEOFENCE ARRIVAL ═══════════════
// Controls automatic arrival detection, timer locking, and trip start gating.
// Driver's live GPS is continuously checked against the pickup coordinates.
export const GEOFENCE_CONFIG = {
  ARRIVAL_RADIUS_METERS: 50,        // Auto-arrival when driver enters this radius
  GPS_DRIFT_GRACE_SECONDS: 15,      // Ignore brief GPS jumps outside the zone
  TIMER_LOCKED_TO_GEOFENCE: true,   // Waiting fee timer only runs inside radius
  TRIP_START_REQUIRES_GEOFENCE: true,// PIN entry blocked if driver is outside radius
  LOCATION_POLL_INTERVAL_MS: 5000,  // Check driver GPS every 5 seconds
  NO_SHOW_GEOFENCE_LOCKED: true,    // 15min no-show clock only counts time inside zone
};

// ═══════════════ LOST ITEM REPORTING ═══════════════
// Bidirectional: rider reports lost, driver reports found — both trip-linked.
// Admin mediates, confirms match, and gates contact sharing.
export const LOST_ITEM_CONFIG = {
  REPORTING_WINDOW_DAYS: 7,         // Rider/driver can report within 7 days of trip
  MAX_PHOTOS: 3,                    // Up to 3 photos per report
  MAX_PHOTO_SIZE_MB: 10,            // 10MB per photo
  REF_PREFIX: 'LI',                 // LI-0001, LI-0002, ...
  DRIVER_DELIVERY_FEE: 20.00,       // $20 if driver delivers to rider
  DRIVER_DROPOFF_FEE: 10.00,        // $10 if driver drops at designated point
  RETURN_DEADLINE_HOURS: 48,        // Driver must return within 48hrs of confirmation
  CONTACT_SHARING: 'admin_gated',   // Never auto-shared, requires admin approval
  STATUSES: [
    'reported',                     // Initial report filed
    'under_review',                 // Admin reviewing
    'confirmed',                    // Admin confirmed item match
    'return_arranged',              // Contact shared, return in progress
    'resolved',                     // Item returned
    'unresolved',                   // Could not be recovered
  ],
};
export const WALLET_CONFIG = {
  CREDIT_SOURCES: {
    REFUND: 'refund',
    GIFT_CARD: 'giftCard',
    ADMIN_CREDIT: 'adminCredit',
    PROMO: 'promo',
  },
  FREE_CASHOUTS_PER_WEEK: 1,
  CASHOUT_FEE: 1.00,        // $1.00 AUD
  MIN_CASHOUT_AMOUNT: 5.00,  // $5.00 AUD
};

// ═══════════════ DRIVER REGISTRATION ═══════════════
export const DRIVER_REG_STEPS = [
  { id: 1, title: 'Personal Details', icon: '👤' },
  { id: 2, title: 'Identity Verification', icon: '🪪' },
  { id: 3, title: 'PT Code', icon: '📜' },
  { id: 4, title: 'Working With Children', icon: '🛡️' },
  { id: 5, title: 'ABN & GST', icon: '🏢' },
  { id: 6, title: 'Vehicle Details', icon: '🚗' },
  { id: 7, title: 'Insurance & Documents', icon: '📋' },
  { id: 8, title: 'Service Preferences', icon: '📍' },
  { id: 9, title: 'Consents & Authorisations', icon: '✅' },
];

// ═══════════════ DOCUMENT TYPES ═══════════════
export const DOCUMENT_TYPES = {
  DRIVERS_LICENCE: { id: 'driversLicence', label: "Driver's Licence", hasExpiry: true },
  DRIVER_AUTHORITY: { id: 'driverAuthority', label: 'Licence copy showing PT Code', hasExpiry: true },
  WWCC: { id: 'wwcc', label: 'Working With Children Check', hasExpiry: true },
  PROOF_OF_IDENTITY: { id: 'proofOfIdentity', label: 'Proof of Identity', hasExpiry: false },
  CTP_INSURANCE: { id: 'ctpInsurance', label: 'CTP Insurance (Green Slip)', hasExpiry: true },
  COMPREHENSIVE_INSURANCE: { id: 'comprehensiveInsurance', label: 'Comprehensive Insurance', hasExpiry: true },
  VEHICLE_REGISTRATION: { id: 'vehicleRegistration', label: 'Vehicle Registration', hasExpiry: true },
};

// ═══════════════ REFERRAL SYSTEM ═══════════════
export const REFERRAL_CONFIG = {
  BONUS_AMOUNT: 200.00,       // $200 AUD
  REQUIRED_TRIPS: 50,         // Trips before payout
  CODE_FORMAT: /^D\d{3}-[A-Z0-9]{4}$/, // e.g., D014-R4K7
};

// ═══════════════ COMMISSION TIERS ═══════════════
export const COMMISSION_TIERS = {
  GOLD: { rate: 0.92, maxCancelRate: 0, label: 'Gold — 92%' },
  STANDARD: { rate: 0.85, maxCancelRate: 0.03, label: 'Standard — 85%' },
  BASIC: { rate: 0.75, maxCancelRate: 1.0, label: 'Basic — 75%' },
};

// ═══════════════ FILE UPLOAD LIMITS ═══════════════
export const UPLOAD_LIMITS = {
  MAX_FILE_SIZE_MB: 10,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  ALLOWED_DOC_TYPES: ['application/pdf', 'image/jpeg', 'image/png'],
  MAX_VEHICLE_PHOTOS: 6,
};

// ═══════════════ NOTIFICATION TYPES ═══════════════
export const NOTIFICATION_TYPES = {
  BOOKING_CONFIRMED: 'bookingConfirmed',
  BOOKING_CANCELLED: 'bookingCancelled',
  DRIVER_ASSIGNED: 'driverAssigned',
  DRIVER_EN_ROUTE: 'driverEnRoute',
  DRIVER_ARRIVED: 'driverArrived',
  TRIP_PIN_READY: 'tripPinReady',           // Rider: your PIN is ready for this trip
  TRIP_PIN_FAILED: 'tripPinFailed',         // Admin: driver failed PIN 3 times
  GEOFENCE_ARRIVED: 'geofenceArrived',      // Rider: driver has arrived at pickup
  GEOFENCE_LEFT: 'geofenceLeft',            // System: driver left pickup zone, timer paused
  TRIP_COMPLETED: 'tripCompleted',
  PAYMENT_RECEIVED: 'paymentReceived',
  DOCUMENT_EXPIRING: 'documentExpiring',
  APPLICATION_UPDATE: 'applicationUpdate',
  WALLET_CREDIT: 'walletCredit',
  LOST_ITEM_REPORTED: 'lostItemReported',   // Other party: item reported on your trip
  LOST_ITEM_CONFIRMED: 'lostItemConfirmed', // Both parties: admin confirmed match
  LOST_ITEM_CONTACT: 'lostItemContact',     // Both parties: contact details shared
  LOST_ITEM_RESOLVED: 'lostItemResolved',   // Both parties: case resolved
};
